<style src="./ConnectionLevel.css"></style>

<template lang="html">
  <section class="connection-wrapper">
    <section class="connection" @click="toggleTooltip">
      <div class="bars">
        <div class="bar one" :class="{'off': connectionLevel < 1, 'yellow': connectionColor === 'yellow', 'red': connectionColor === 'red'}"></div>
        <div class="bar two" :class="{'off': connectionLevel < 2, 'yellow': connectionColor === 'yellow', 'red': connectionColor === 'red'}"></div>
        <div class="bar three" :class="{'off': connectionLevel < 3, 'yellow': connectionColor === 'yellow', 'red': connectionColor === 'red'}"></div>
        <div class="bar four" :class="{'off': connectionLevel < 4, 'yellow': connectionColor === 'yellow', 'red': connectionColor === 'red'}"></div>
        <div class="bar five" :class="{'off': connectionLevel < 5, 'yellow': connectionColor === 'yellow', 'red': connectionColor === 'red'}"></div>
      </div>
      <div class="conn-text"
      :class="{
        'off': connectionLevel < 1,
        'yellow': connectionColor === 'yellow',
        'red': connectionColor === 'red'}"
      >{{ connectionLabel }}</div>
    </section>

    <!-- TOOLTIP -->
    <section class="connection-tooltip" v-if="showTooltip">
      <div class="tooltip-header">
        <h3>Informações da Conexão</h3>
        <i class="fas fa-times" @click="toggleTooltip"></i>
      </div>
      <div class="tooltip-content">
        <div class="tooltip-section">
          <h4>Status da Conexão</h4>
          <div class="tooltip-item">
            <span class="tooltip-label">Nível:</span>
            <span class="tooltip-value" :class="connectionColor">{{ connectionLabel }}</span>
          </div>
          <div class="tooltip-item">
            <span class="tooltip-label">Qualidade:</span>
            <span class="tooltip-value">{{ connectionLevel }}/5</span>
          </div>
        </div>
        
        <div class="tooltip-divider"></div>
        
        <div class="tooltip-section" v-if="objAgora">
          <h4>Informações da Sala</h4>
          <div class="tooltip-item">
            <i class="fas fa-door-open"></i>
            <span class="tooltip-value">{{ objAgora.nome || 'Sem nome de sala' }}</span>
          </div>
          <div class="tooltip-item">
            <i class="fas fa-user"></i>
            <span class="tooltip-value">1º - {{ objAgora.primaryUID || 'Sem UID' }}</span>
          </div>
          <div class="tooltip-item">
            <i class="far fa-user"></i>
            <span class="tooltip-value">2º - {{ objAgora.secondaryUID || 'Sem UID' }}</span>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
export default {
  name: "ConnectionLevel",
  components: {
  },

  props: {
    objConnection: {
      type: Object,
      default: {}
    },
    objAgora: {
      type: Object,
      default: null
    },
    inChannel: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      connectionColor: 'yellow',
      connectionLevel: 3,
      connectionLabel: 'Medindo conexão...',
      wasLoggedBelowThreshold: false,
      previousLevel: 3,

      intervalId: null,
      rttArray: [],
      levelArray: [],
      
      showTooltip: false
    };
  },

  computed: {},

  watch: {
    objConnection (val) {
      this._handleObjConnection(val)
    },
    inChannel(val) {
      if (val) {
        this._handleObjConnection(this.objConnection);
      }
    },
  },

   beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  },

  async mounted () {
    this._handleObjConnection(this.objConnection)
  },

  methods: {
    /**
     * Processa as informações de qualidade de conexão recebidas e atualiza o estado atual.
     * - Calcula o nível de qualidade da conexão com base nos dados de uplink e downlink.
     * - Atualiza o status da conexão e armazena o nível anterior para comparações futuras.
     * @private
     * @param {Object} obj - Objeto contendo informações sobre a qualidade da conexão.
     * @returns {void} Atualiza as propriedades internas da instância com base nos dados da conexão.
     */
    _handleObjConnection (obj) {
      const down = obj?.downlinkNetworkQuality;
      const up = obj?.uplinkNetworkQuality;

      if (down == null || up == null) {
        return;
      }

      const downNum = Number(down);
      const upNum = Number(up);

      if (!Number.isFinite(downNum) || !Number.isFinite(upNum)) {
        return;
      }

      if (downNum === 6 || upNum === 6) {
        this.connectionLevel = 0;
      } else if (downNum === 0 && upNum === 0) {
        if (this.inChannel) {
          this.connectionLevel = 4;
          this.connectionColor = '';
          this.connectionLabel = 'Conectado à sala';
          this.previousLevel = 4;
          return;
        }
        if (this.previousLevel > 0) {
          this.connectionLevel = this.previousLevel;
        } else {
          this.connectionLevel = 3;
          this.connectionColor = 'yellow';
          this.connectionLabel = 'Medindo conexão...';
          this.previousLevel = 3;
          return;
        }
      } else {
        const downForCalc = downNum === 0 ? upNum : downNum;
        const upForCalc = upNum === 0 ? downNum : upNum;
        this.connectionLevel = this._calculateConnectionQuality({
          downlinkNetworkQuality: downForCalc,
          uplinkNetworkQuality: upForCalc,
        });
      }

      obj.level = this.connectionLevel;
      this._updateConnectionStatus(this.connectionLevel);
      this.previousLevel = this.connectionLevel || 0;
    },

    /**
     * Atualiza o status da conexão com base no nível de conexão fornecido.
     * @private
     * @param {number} level - Nível da conexão (1 a 5), onde 1 e 2 são instáveis,
     * 3 é estável com aviso e 4 e 5 são estáveis.
     */
    _updateConnectionStatus(level) {
      switch (level) {
        case 1:
        case 2:
          this.connectionColor = 'red';
          this.connectionLabel = 'Conexão Instável';
          break;
        case 3:
          this.connectionColor = 'yellow';
          this.connectionLabel = 'Conexão Estável';
          break;
        case 4:
        case 5:
          this.connectionColor = '';
          this.connectionLabel = 'Conexão Estável';
          break;
        default:
          this.connectionColor = 'red';
          this.connectionLabel = 'Sem Conexão';
          break;
      }
    },

    /**
    * Calcula o nível de qualidade da conexão com base em vários parâmetros.
    * @private
    * @param {Object} params - Os parâmetros da conexão.
    * @returns {number} - O nível de qualidade da conexão (de 1 a 5).
    */
    _calculateConnectionQuality ({ downlinkNetworkQuality, uplinkNetworkQuality }) {
      const qualityAverage = Math.ceil(
          (downlinkNetworkQuality + uplinkNetworkQuality) / 2
      )
      switch (qualityAverage) {
        case 1:
          return 5
        case 2:
          return 4
        case 3:
          return 3
        case 4:
          return 2
        case 5:
          return 1
        default:
          return 0
      }
    },

    /**
     * Alterna a visibilidade do tooltip
     */
    toggleTooltip() {
      this.showTooltip = !this.showTooltip;
    }
  },
};
</script>
